import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  loadMergedCms,
  saveFullCms,
  saveCmsPatch,
  resetCmsToDefaults,
  exportCmsJson,
  importCmsJsonString,
} from "../utils/cmsStorage";
import { pullSiteCmsFromServer, hydrateCmsIntoStorage } from "../utils/cmsBootstrap";

const CmsContext = createContext(null);

export function CmsProvider({ children }) {
  const [cms, setCms] = useState(() => loadMergedCms());

  useEffect(() => {
    const ac = new AbortController();
    pullSiteCmsFromServer(ac.signal).then((remoteMerged) => {
      if (ac.signal.aborted || remoteMerged == null) return;
      setCms(hydrateCmsIntoStorage(remoteMerged));
    });
    return () => ac.abort();
  }, []);

  const refresh = useCallback(() => {
    setCms(loadMergedCms());
  }, []);

  const replaceCms = useCallback((full) => {
    saveFullCms(full);
    setCms(full);
  }, []);

  const patchCms = useCallback((partial) => {
    const next = saveCmsPatch(partial);
    setCms(next);
    return next;
  }, []);

  const reset = useCallback(() => {
    const next = resetCmsToDefaults();
    setCms(next);
    return next;
  }, []);

  const importJson = useCallback((str) => {
    const next = importCmsJsonString(str);
    setCms(next);
    return next;
  }, []);

  const exportJson = useCallback(() => exportCmsJson(loadMergedCms()), []);

  const value = useMemo(
    () => ({
      cms,
      refresh,
      replaceCms,
      patchCms,
      reset,
      exportJson,
      importJson,
    }),
    [cms, refresh, replaceCms, patchCms, reset, exportJson, importJson]
  );

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms() {
  const ctx = useContext(CmsContext);
  if (!ctx) {
    throw new Error("useCms must be used within CmsProvider");
  }
  return ctx;
}
